import React, { FC, useMemo } from 'react';
import TextField, { TextFieldProps } from '@material-ui/core/TextField';
import { IMaskMixin } from 'react-imask';
import { ReadOnly } from '../ReadOnly/ReadOnly';
import { useSmartFormContext } from '../../forms/utils/useSmartFormContext';
import { disabledAutoComplete, useAnonymousName } from '../utils/disableBrowserAutocomplete';
import { AtomWrapper } from '../AtomWrapper/AtomWrapper';
import { InfoText } from '../InfoText/InfoText';
import { PermissionHelper } from '../common/PermissionHelper';
import { SimplifiedInputMaskProps } from './types';
import styled from 'styled-components';

const MaskedInput = IMaskMixin(({ inputRef, ...props }: any) => (
  <TextField inputRef={inputRef} name={props.name} {...props} />
)) as FC<SimplifiedInputMaskProps & TextFieldProps>;

export type TextFieldAtomProps = TextFieldProps & {
  name: string;
  mask?: SimplifiedInputMaskProps['mask'];
  maskProps?: SimplifiedInputMaskProps;
  className?: string;
  infoText?: string;
  disablePermissionDebugger?: boolean;
  forceError?: boolean;
};

export const TextFieldAtom: React.FC<React.PropsWithChildren<TextFieldAtomProps>> = (props) => {
  const { getDisabled, readonly, submitCount, getFieldPermissions, permissionDebugger } = useSmartFormContext();
  const id = useAnonymousName(props.name ?? 'textField');
  const {
    mask,
    maskProps,
    onChange,
    onBlur,
    label,
    infoText,
    inputProps,
    disablePermissionDebugger,
    forceError,
    ...rest
  } = props;
  const fullWidth = props.fullWidth === undefined ? true : props.fullWidth;
  const value = props.value === undefined || props.value === null ? '' : (props.value as any).toString();
  const error = forceError ? !!props.error : submitCount > 0 && !!props.error;
  const appliedInputProps = { ...inputProps, className: `id_${props.name}` };

  const disabled = useMemo(() => props.disabled || getDisabled(props.name), [props.disabled, props.name, getDisabled]);
  const permissions = getFieldPermissions(props.name);

  if (readonly && !maskProps) {
    return (
      <AtomWrapper name={props.name}>
        <ReadOnly
          type={(props.type as any) || 'text'}
          permissionsId={id}
          infoText={infoText}
          multiline={props.multiline}
          fullWidth={fullWidth}
          label={props.label}
          value={value}
          className={props.className}
          error={error && props.helperText}
          permission={permissions}
          disablePermissionDebugger={disablePermissionDebugger}
        />
      </AtomWrapper>
    );
  }

  const formHelperTextProps = {
    'data-test': `${props.name}-field-helper`,
    id: `${props.name}-helper-text`
  };

  return (
    <AtomWrapper name={props.name}>
      {!!mask || !!maskProps ? (
        <StyledMaskedInput
          label={label && <InfoText label={label} required={props.required} tooltipText={infoText} />}
          InputLabelProps={{ shrink: true }}
          {...rest}
          inputProps={appliedInputProps}
          autoComplete={disabledAutoComplete.autoComplete}
          value={props.value as string}
          fullWidth={fullWidth}
          disabled={disabled || readonly}
          $readonly={readonly}
          id={id}
          name={id}
          FormHelperTextProps={formHelperTextProps}
          mask={mask}
          unmask
          {...(maskProps ?? {})}
          onAccept={(value: string) => {
            if (!maskProps?.allowNegative) {
              value = value.replace('-', '');
            }
            onChange?.({ target: { value }, currentTarget: { value } } as any);
          }}
          onBlur={onBlur}
        />
      ) : (
        <TextField
          label={label && <InfoText label={label} required={props.required} tooltipText={infoText} />}
          InputLabelProps={{ shrink: true }}
          {...rest}
          inputProps={appliedInputProps}
          autoComplete={disabledAutoComplete.autoComplete}
          value={value}
          onChange={onChange}
          minRows={props.multiline ? 5 : 1}
          onBlur={onBlur}
          fullWidth={fullWidth}
          id={id}
          name={id}
          disabled={disabled}
          error={error}
          FormHelperTextProps={formHelperTextProps}
        />
      )}
      {permissionDebugger && !disablePermissionDebugger && <PermissionHelper permission={permissions} />}
    </AtomWrapper>
  );
};

const StyledMaskedInput = styled(MaskedInput)<{ $readonly?: boolean }>`
  && {
    ${({ $readonly }) =>
      $readonly
        ? `
      .Mui-disabled {
        background-color: transparent;
        border: none;
        padding: 0;
      }
      .Mui-disabled:focus {
        outline: none;
      }
    `
        : ''}
  }
`;
